"use client";

import {
  type ChangeEvent,
  useCallback,
  useState,
} from "react";
import { Slider } from "@/components/ui/slider";
import { clamp } from "@/utils/roiCalculator";

export function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  formatMin,
  formatMax,
  formatValue,
  id,
}: Readonly<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  formatMin: string;
  formatMax: string;
  formatValue: (v: number) => string;
  id: string;
}>) {
  const [localInput, setLocalInput] = useState<string | null>(null);

  const handleSliderChange = useCallback(
    (values: number[]) => {
      onChange(values[0]);
    },
    [onChange],
  );

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setLocalInput(e.target.value);
  }, []);

  const commitInput = useCallback(() => {
    if (localInput !== null) {
      const raw = localInput.replaceAll(/\D/g, "");
      const parsed = Number(raw);
      if (!Number.isNaN(parsed) && parsed > 0) {
        onChange(clamp(parsed, min, max));
      }
      setLocalInput(null);
    }
  }, [localInput, onChange, min, max]);

  const displayValue = localInput ?? formatValue(value);
  const labelId = `${id}-label`;
  const inputId = `${id}-input`;

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center gap-4">
        <label
          id={labelId}
          htmlFor={inputId}
          className="font-sans font-medium text-gray-700 text-sm"
        >
          {label}
        </label>
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          value={displayValue}
          onChange={handleInputChange}
          onBlur={commitInput}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitInput();
          }}
          className="bg-white px-3 py-1.5 border border-gray-200 focus:border-gail-primary rounded-lg focus:outline-none focus:ring-1 focus:ring-gail-primary w-24 font-sans font-semibold text-gray-900 text-sm text-right transition-colors"
        />
      </div>
      <Slider
        id={id}
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={handleSliderChange}
        aria-labelledby={labelId}
      />
      <div className="flex justify-between font-sans text-gail-gray text-xs">
        <span>{formatMin}</span>
        <span>{formatMax}</span>
      </div>
    </div>
  );
}
